/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import React, { useRef } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { cx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Grid, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { getUnifiedSearchRightRailEnhancementsExpValue } from '../../../../utils/experiments';
import { useAccordion } from '../../../accordion';
import { ResultCount } from '../result-count';
import { ResultCountLoading } from '../result-count-loading';

type AccordionFilterTriggerProps = {
	icon: React.ReactNode;
	label: React.ReactNode;
	count?: number;
	countLoading?: boolean;
};

const accordionFilterTriggerStyles = xcss({
	alignItems: 'center',
	display: 'flex',
	width: '100%',
	paddingLeft: 'space.150',
	paddingRight: 'space.150',
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
	color: 'color.text',
	gap: 'space.100',
	':hover': {
		background: token('color.background.neutral.subtle.hovered'),
	},
});

// Single-select variant styles
const accordionFilterTriggerStylesSingleSelect = xcss({
	alignItems: 'center',
	display: 'flex',
	width: '100%',
	paddingLeft: 'space.100',
	paddingRight: 'space.100',
	paddingTop: 'space.075',
	paddingBottom: 'space.075',
	color: 'color.text',
	gap: 'space.075',
	borderRadius: 'radius.small',
	':hover': {
		background: 'background.neutral.subtle.hovered',
	},
});

const accordionFilterTriggerSelectedStyles = xcss({
	color: 'color.text.accent.blue',
	background: token('color.background.selected'),

	':hover': {
		color: 'color.text.accent.blue',
		background: token('color.background.selected'),
	},
});

// Single-select variant selected styles — adds border radius to match the hover state
const accordionFilterTriggerSelectedStylesSingleSelect = xcss({
	color: 'color.text.accent.blue',
	background: token('color.background.selected'),
	borderRadius: 'radius.small',

	':hover': {
		color: 'color.text.accent.blue',
		background: token('color.background.selected'),
	},
});

const iconStyles = xcss({
	minWidth: '24px',
	minHeight: '24px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

// Single-select variant icon styles
const iconStylesSingleSelect = xcss({
	width: 'size.200', // 16px
	height: 'size.200', // 16px
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

// Single-select variant chevron wrapper — constrains the chevron SVG to 16px to match app icon size
const chevronWrapperStylesSingleSelect = xcss({
	width: 'size.200', // 16px
	height: 'size.200', // 16px
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

const contentStyles = xcss({
	width: '100%',
	flex: 1,
});

const labelStyles = cssMap({
	truncated: {
		textOverflow: 'ellipsis',
		textWrap: 'nowrap',
		overflow: 'hidden',
		textAlign: 'start',
	},
});

export const AccordionFilterTrigger: ({
	icon,
	label,
	count,
	countLoading,
}: AccordionFilterTriggerProps) => JSX.Element = ({
	icon,
	label,
	count,
	countLoading,
}: AccordionFilterTriggerProps) => {
	const { displayChevron, isOpen, hasSelection } = useAccordion();
	const labelRef = useRef<HTMLDivElement>(null);

	const shouldTooltipAppear = () =>
		(labelRef.current?.offsetWidth ?? 0) < (labelRef.current?.scrollWidth ?? 0);

	const renderChevron = () => {
		// We are reversing the behavior (down arrow if accordian is open, right arrow if accordian is closed) for the Chevron icon in this experiment
		// to match other Atlassian components that use accordion triggers.
		// Note: ChevronIcon size prop does not accept "xsmall", so we wrap in a Box to constrain to 16px to match app icon sizing
		if (getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select') {
			return (
				<>
					{displayChevron && isOpen && (
						<Box xcss={chevronWrapperStylesSingleSelect}>
							<ChevronDownIcon label="" size="small" />
						</Box>
					)}
					{displayChevron && !isOpen && (
						<Box xcss={chevronWrapperStylesSingleSelect}>
							<ChevronRightIcon label="" size="small" />
						</Box>
					)}
					{!displayChevron && icon}
				</>
			);
		}

		return (
			<>
				{displayChevron && isOpen && <ChevronRightIcon label="" size="small" />}
				{displayChevron && !isOpen && <ChevronDownIcon label="" size="small" />}
				{!displayChevron && icon}
			</>
		);
	};

	return (
		<Box
			xcss={[
				getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
					? accordionFilterTriggerStylesSingleSelect
					: accordionFilterTriggerStyles,
				hasSelection &&
					!isOpen &&
					(getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
						? accordionFilterTriggerSelectedStylesSingleSelect
						: accordionFilterTriggerSelectedStyles),
			]}
		>
			<Box
				xcss={
					getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
						? iconStylesSingleSelect
						: iconStyles
				}
				aria-hidden
			>
				{renderChevron()}
			</Box>

			<Box xcss={contentStyles}>
				<Tooltip content={label} delay={300} canAppear={shouldTooltipAppear} hideTooltipOnClick>
					<Grid
						templateColumns="1fr max-content"
						justifyContent="center"
						alignItems="center"
						gap={
							getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
								? 'space.075'
								: 'space.100'
						}
					>
						<Box xcss={cx(labelStyles.truncated)} ref={labelRef}>
							<Text color="inherit">{label}</Text>
						</Box>
						{countLoading ? (
							<ResultCountLoading />
						) : (
							<ResultCount count={count} isSelected={hasSelection} />
						)}
					</Grid>
				</Tooltip>
			</Box>
		</Box>
	);
};
